import { useEffect, useState } from "react";
import Radio from "backbone.radio";
import classnames from "classnames";
import { ReactToMarionetteView } from "extensions/marionette";
import { captureBreadcrumb, captureException, lastEventId } from "extensions/sentry";
import styled from "styled-components";

import { store } from "common/store";

import { CancelButton, PrimaryButton } from "common/components/buttons";

import {
    UnableToLoadChunk,
    CannotConnectToAPIServerError
} from "common/helpers/errors";

import logo from "img/logo-color@2x.png";

import "./error.scss";

const DEFAULT_HEADER = "Let's try this again";
const DEFAULT_MESSAGE = "Please refresh the page, as data could not load.";

export function ErrorView(props) {
    const [error_id, setError_id] = useState(null);
    const app_ready = store.getState().config.ready;
    
    useEffect(() => {
        if (props.error instanceof Error && props.captureInSentry) {
            captureBreadcrumb({
                message: "Error shown. componentDidMount in ErrorView",
                data: {
                    inline: props.inline,
                    custom: props.custom,
                    header: typeof props.header === "string" ? props.header : props.error?.header,
                    message: props.message || props.error?.message,
                    extra: props.error.extra
                }
            });
            
            captureException(props.error);
        }
        
        setError_id(lastEventId());

        if (!props.inline) {
            document.body.classList.add("has-error");
            if (app_ready) {
                document.body.classList.add("logged-in-error");
            } else {
                document.body.classList.add("not-logged-in-error");
            }
        }

        return () => {
            if (!props.inline) {
                document.body.classList.remove("has-error");
                document.body.classList.remove("logged-in-error");
                document.body.classList.remove("not-logged-in-error");
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const getErrorDetails = () => {
        if (error_id) {
            // eslint-disable-next-line sentence-case/sentence-case
            return <div className="error-id">Error ID: {error_id}</div>;
        }
        
        return "";
    };
    
    const getEmailHref = () => {
        if (error_id) {
            return `mailto:help@pazien.com?body=Error%20ID%3A%20${error_id}`;
        }
    
        return "mailto:help@pazien.com";
    };
    
    const renderDefaultActions = () => (
        <div className="error-actions">
            {!props.error.preventGoBack && <CancelButton onClick={() => { window.history.go(-1); }}>Go back</CancelButton>}
            <PrimaryButton onClick={ErrorView.reloadPage}>Refresh page</PrimaryButton>
        </div>
    );

    // Render
    let header;
    let message;
    
    let actions = renderDefaultActions();
    
    if (props.custom || props.error.custom) {
        message = props.message || props.error.message || DEFAULT_MESSAGE;
        header = typeof props.header === "string" ? props.header : props.error.header || DEFAULT_HEADER;
        
        if (!props.error || props.error.actions === false || props.actions === false) {
            actions = null;
        } else if (Array.isArray(props.error.customActions)) {
            actions = (
                <div className="error-actions">
                    {props.error.customActions.map(({ className, label, onClick = () => {} }) => <PrimaryButton key={className} onClick={onClick} className={className}>{label}</PrimaryButton>)}
                </div>
            );
        }
    } else {
        header = props.header || DEFAULT_HEADER;
        message = props.message || DEFAULT_MESSAGE;
    }

    if (props.error instanceof CannotConnectToAPIServerError) {
        return (
            <ServerErrorWrapper className={classnames("pazien-error", `inline-${props.inline || false}`)}>
                <Logo>Pazien</Logo>
                <div className="error-header">We are experiencing an issue</div>
                <div>
                    <div className="message m-top-10">The Pazien Team is working to resolve the problem as quickly as possible.</div>
                    <div className="message m-top-65">Please try again shortly. If problem persists, contact <a className="weight-700" href="mailto:help@pazien.com">help@pazien.com</a>.</div>
                    <div className="message weight-700 m-top-80">We apologize for the inconvenience.</div>
                    <div className="message small-message m-top-100">{message}</div>
                </div>
            </ServerErrorWrapper>
        );
    }
    
    return (
        <div className={classnames("pazien-error", `inline-${props.inline || false}`)}>
            <div className="error-header">{header}</div>
            <div className="error-message">{message}</div>
            {!props.error.hideDetails && !props.hideDetails && store.getState().config.debug && props.error?.stack && (
                <div className="error-stack"><pre>{props.error.stack}</pre></div>
            )}
            {props.error.info && (
                <div className="error-info">{props.error.info}</div>
            )}
            {actions}
            {(!props.hideDetails && !props.error.hideDetails && props.captureInSentry) && (
                <div className="error-bottom">Pazien developers were notified of this issue, but if problems persist, email <a href={getEmailHref()}>help@pazien.com</a>. {getErrorDetails() }</div>
            )}
        </div>
    );
}

ErrorView.reloadPage = () => {
    window.location.reload();
};

ErrorView.defaultProps = {
    inline: true,
    captureInSentry: true,
    custom: false,
    hideDetails: false,
    error: {}
};

export const makeMnErrorView = function(error, inline = false, captureInSentry = true) {
    return new ReactToMarionetteView({
        dumb: true,
        component: <ErrorView error={error} inline={inline} captureInSentry={captureInSentry} />
    });
};

export const showError = function(error) {
    Radio.channel("app").request("error:show", error);
};

export const ensureErrorCallback = (err, callback) => {
    const numberOfReloads = Number.parseInt(localStorage.getItem("reloaded") || 0, 10);
    
    // If app is running in debug mode or there were 3 reloads already then show error message
    if (numberOfReloads > 3 || store.getState().config.debug) {
        // Show the error in console for debugging
        captureBreadcrumb({
            message: "About to display ensureErrorCallback error",
            data: {
                numberOfReloads,
                error_name: err.name,
                error_message: err.message,
                error_stack: err.stack
            }
        });
        
        if (typeof callback === "function") {
            callback();
        } else {
            Radio.channel("main").request(
                "show:app:view", (
                    <ErrorView
                        error={new UnableToLoadChunk({
                            custom: true,
                            message: "For some reason, we couldn't load your page. Try refreshing your browser.",
                            originalError: err,
                            extra: {
                                message: err.message,
                                stack: err.stack // TODO: Do we still need stack and message here?
                            }
                        })}
                        inline={false}
                    />
                ), {
                    dumb: true
                }
            );
        }
    } else {
        // Set counter in localstorage. It will be wipedout on next logout
        localStorage.setItem("reloaded", (numberOfReloads + 1).toString());
        
        // Reload the page in prod
        window.location.reload();
    }
};

const ServerErrorWrapper = styled.div`
    .error-header {
        line-height: 49px;
    }
    .message {
        text-align: center;
        line-height: 27px;
    }
    .weight-700 {
        font-family: "nunito_sans_bold", sans-serif
    }
    .small-message {
        font-size: 12px;
        line-height: 16px;
    }
    .m-top-10 {
        margin-top: 10px;
    }
    .m-top-65 {
        margin-top: 65px;
    }
    .m-top-80 {
        margin-top: 80px;
    }
    .m-top-100 {
        margin-top: 100px;
    }
`;

const Logo = styled.div`
    margin-top: 10px;
    text-align: center;
    text-indent: -9999px;
    background: transparent url(${logo}) 50% 0 no-repeat;
    background-size: 145px;
    height: 41px;
    width: 145px;
    margin-bottom: 100px;
`;
